import React, { createContext, useContext } from 'react';
import {
	isResponseStatus,
	ppdfConsentDetailQuery,
	useAxiosInstance,
	usePoQuery,
} from '@gov-nx/api';
import { useDownloadEvents } from '@gov-nx/core/events';
import { useProcessControl } from '@gov-nx/core/hooks';
import type { Nullable } from '@gov-nx/core/types';
import { toDateReadable } from '@gov-nx/utils/common';
import type {
	PpdfConsentDetailContext,
	PpdfConsentDetailControl,
} from './context.types';

const PpdfSouhlasyDetailContext =
	createContext<Nullable<PpdfConsentDetailContext>>(null);

interface PpdfSouhlasyDetailContextProviderProps {
	consentId?: Nullable<string>;
	children: React.ReactNode;
}

export function PpdfSouhlasyDetailContextProvider({
	consentId,
	children,
}: PpdfSouhlasyDetailContextProviderProps) {
	const { downloadPpdfConsent } = useDownloadEvents();
	const { axiosInstance } = useAxiosInstance();
	const { setControls, controls } = useProcessControl<PpdfConsentDetailControl>(
		{
			isDownloadProcessing: false,
		}
	);

	const query = usePoQuery(
		{
			queryKey: ['ppdf-consent-detail', consentId],
			queryFn: () =>
				consentId ? ppdfConsentDetailQuery(axiosInstance, consentId) : null,
		},
		{
			errorIgnoreFilter: isResponseStatus(404),
		}
	);

	const detail = query.data;

	const handlePpdfConsentDownload = () => {
		if (!consentId) return;
		setControls({
			isDownloadProcessing: true,
		});
		return downloadPpdfConsent({
			consentId,
			callback: () => {
				setControls({
					isDownloadProcessing: false,
				});
			},
		});
	};

	return (
		<PpdfSouhlasyDetailContext.Provider
			value={{
				consentId,
				// TODO: this is really bad. We need to find a better way how to handle this everywhere
				isError: query.isError,
				noData: query.error != null && isResponseStatus(404)(query.error),
				isLoading: query.isLoading,
				fromOvmName: detail?.spravceUdaju,
				fromAgendaName: detail?.agendaSpravceUdaju,
				toOvmName: detail?.prijemceUdaju,
				toAgendaNames: detail?.agendyPrijemceUdaju?.join(', '),
				sharedAgendaData: detail?.udaje,
				fyzickaOsoba: detail?.osoba?.fyzickaOsoba,
				pravnickaOsoba: detail?.osoba?.pravnickaOsoba,
				datumTrvaniUdeleniSouhlasu: detail?.datumTrvaniUdeleniSouhlasu
					? toDateReadable(detail.datumTrvaniUdeleniSouhlasu)
					: undefined,
				datumUdeleniSouhlasu: detail?.datumUdeleniSouhlasu
					? toDateReadable(detail.datumUdeleniSouhlasu)
					: undefined,
				handlePpdfConsentDownload,
				controls,
			}}>
			{children}
		</PpdfSouhlasyDetailContext.Provider>
	);
}

export const usePpdfSouhlasyDetailContext = (): PpdfConsentDetailContext =>
	useContext(PpdfSouhlasyDetailContext) as PpdfConsentDetailContext;
